import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  Stack,
  Chip,
  Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ReplayIcon from '@mui/icons-material/Replay';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';

import { AuthContext } from 'utils/AuthContext';

import { syncProfile } from 'store/slices/adminSlice';
import {
  callArchiveDrChronoJobEvent,
  getDrChronoEventHistoryDetails,
} from 'store/slices/patientInfoSlice';

import { useAppDispatch } from 'store/hooks';
import { toast } from 'react-toastify';
import { ROUTE } from 'route/RouteEnums';
import { Link } from 'react-router-dom';
import { arch } from 'os';
import ApplicationHealthDashboard from './ApplicationHealthDashboard';
import FailedEventsStatusRow from './FailedEventsStatusRow';
import ArchiveJobDialog from './ArchiveJobDialog';
import { validateSession } from 'services/validateSession';

/**
 * MM-1088: event types the archive API (ehr-sync-service archiveJobById) actually archives. For
 * any other type the API returns 200 but leaves the job as it is, so the Archive button is shown
 * only for these.
 */
const ARCHIVABLE_EVENT_TYPES = [
  'CREATE_PATIENT',
  'MFSH_CUSTOM_FORM',
  'WELLNESS_SERVEY_FORM',
  'UPDATE_VITALS',
];

/**
 * MM-1088: true when the job's event type can be archived (see ARCHIVABLE_EVENT_TYPES).
 */
const isArchivable = (eventType?: string) =>
  !!eventType && ARCHIVABLE_EVENT_TYPES.includes(eventType.toUpperCase());

/**
 * MM-1088: returns the patient rows without the given patient's row (used after a retry). Kept outside
 * the component so the state update does not nest functions too deeply (Sonar).
 */
const removePatientRow = (rows: any[], patientId: number) => rows.filter((row) => row.patientId !== patientId);

/**
 * MM-1088: returns the patient rows without one archived job. The patient's job count goes down, and the
 * patient row is dropped when it was the patient's last job. Kept outside the component so the state
 * update does not nest functions too deeply (Sonar).
 */
const removeJobFromRows = (rows: any[], patientId: number, jobId: number) =>
  rows
    .map((row) => {
      if (row.patientId !== patientId) return row;
      const jobs = (row.jobs ?? []).filter((j: any) => j.jobId !== jobId);
      return { ...row, jobs, jobCount: jobs.length };
    })
    .filter((row) => row.jobs.length > 0);

// This file intentionally avoids using @mui/x-data-grid-pro or other "prod" grid libs.
// It implements a simple table with expand/collapse (accordion) rows using MUI core only.

export default function PatientJobsTable() {
  const { user, setUserDetails } = useContext(AuthContext);
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState<boolean>(true);
  const [patientData, setPatientData] = useState<any>([]);
  /**
   * MM-1088: true when the last failed events load failed, so the page says so instead of
   * showing an empty table.
   */
  const [loadError, setLoadError] = useState<boolean>(false);
  /**
   * MM-1088: the job (and its patient) the user clicked Archive on; opens the confirm dialog.
   */
  const [archiveTarget, setArchiveTarget] = useState<{ job: any; patientId: number } | null>(
    null
  );
  /**
   * MM-1088: true while the archive call runs, so the dialog buttons can't be clicked twice.
   */
  const [archiving, setArchiving] = useState<boolean>(false);

  const formatDate = (val: number) => {
    try {
      return new Date(val).toLocaleString();
    } catch {
      return String(val);
    }
  };

  const splitFailure = (msg?: string) => {
    if (!msg) return [];
    // split before sequences like "0 :" or "1 :" etc and trim
    return msg
      .split(/(?=\d+\s*[:])/g)
      .map((s) => s.trim())
      .filter(Boolean);
  };

  // allow multiple rows expanded at once
  const [expandedIds, setExpandedIds] = useState<number[]>([]);

  const toggleExpand = (id: number) => {
    setExpandedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  useEffect(() => {
    getDrChronoEventHistory();
  }, []);

  /**
   * MM-1088: loads the failed events list. Shows the loading row while the call runs, and the
   * error row (with "Try again") if the call fails or returns a non-200 status, instead of
   * leaving the table blank.
   */
  function getDrChronoEventHistory() {
    setLoading(true);
    setLoadError(false);
    dispatch(getDrChronoEventHistoryDetails({}))
      .unwrap()
      .then((response: any) => {
        if (response?.status === 200) {
          setPatientData(response?.data);
          showPendingEventsToast(response?.data?.pageResults);
        } else {
          setLoadError(true);
        }
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }
  /**
   * MM-1088: after the list loads, shows a warning toast with how many failed events (and for
   * how many patients) are waiting, so the user knows at once there is work on the page.
   * Shows nothing when there are no failed events.
   */
  function showPendingEventsToast(pageResults?: any[]) {
    if (!pageResults?.length) return;
    const jobs = pageResults.reduce((sum: number, row: any) => sum + (row.jobCount ?? 0), 0);
    const patients = pageResults.length;
    toast(`Pending failed DrChrono events: ${jobs} (patients: ${patients})`, {
      type: 'warning',
      icon: true,
    });
  }
  function jobRetry(patientId: number) {
    dispatch(syncProfile(patientId))
      .unwrap()
      .then((res: any) => {
        if (res.status === 200) {
          toast('Job retried successfully', {
            type: 'success',
            icon: true,
          });

          // ⬇️ REMOVE ROW FROM TABLE
          setPatientData((prev: any) => ({
            ...prev,
            pageResults: removePatientRow(prev.pageResults, patientId),
          }));
        }
      });
  }

  /**
   * MM-1088: archives the one job chosen in the confirm dialog with the existing archive API
   * (jobId + patientId). On success the job is removed from its patient's list and the job count
   * goes down; the patient row is removed when it was the patient's last job. Other jobs of the
   * same patient stay on the page.
   */
  function archiveJob() {
    if (!archiveTarget) return;
    const { job, patientId } = archiveTarget;
    setArchiving(true);
    dispatch(callArchiveDrChronoJobEvent({ jobId: job.jobId, patientId }))
      .unwrap()
      .then((response: any) => {
        if (response?.status === 200) {
          removeJobFromTable(job.jobId, patientId);
          toast('Job archived successfully', {
            type: 'success',
            icon: true,
          });
        } else {
          toast('Could not archive the job. Please try again.', { type: 'error', icon: true });
        }
      })
      .catch(() =>
        toast('Could not archive the job. Please try again.', { type: 'error', icon: true })
      )
      .finally(() => {
        setArchiving(false);
        setArchiveTarget(null);
      });
  }
  /**
   * MM-1088: removes one archived job from the table without reloading the page.
   */
  function removeJobFromTable(jobId: number, patientId: number) {
    setPatientData((prev: any) => ({
      ...prev,
      pageResults: removeJobFromRows(prev.pageResults, patientId, jobId),
    }));
  }
  function copyJob(job: any) {
    if (!navigator.clipboard) return alert('Clipboard not supported');
    navigator.clipboard.writeText(JSON.stringify(job, null, 2));
  }
  const rowSelect = async (params: any) => {
    await validateSession();
    window.open(`${ROUTE.ADMIN_PATIENT_INFO}/${params.row.patientId}`, '_blank');
    const data = {
      ...user,
      patientId: params.row.patientId,
    };
    setUserDetails(data);
  };

  return (
    <Container maxWidth='lg'>
      <Grid item xs={12}>
        <Typography
          variant='h5'
          position={'relative'}
          width={'fit-content'}
          sx={{ paddingTop: 6, textTransform: 'capitalize' }}
        >
          Patient Sync Jobs
        </Typography>
      </Grid>
      <Grid item xs={12} sx={{ padding: 2, borderRadius: 2 }}>
        <Paper elevation={0} sx={{ width: '100%' }}>
          <TableContainer>
            <Table stickyHeader size='small' aria-label='patient-jobs-table'>
              <TableHead>
                <TableRow sx={{ boxShadow: '0px 4px 4px -2px rgba(0,0,0,0.2)', mb: 2 }}>
                  <TableCell sx={{ width: 72 }} />
                  <TableCell>Patient ID</TableCell>
                  <TableCell>Patient Name</TableCell>
                  <TableCell>Jobs</TableCell>
                  <TableCell align='center'>Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {/* MM-1088: loading / error / no failed events message */}
                <FailedEventsStatusRow
                  loading={loading}
                  loadError={loadError}
                  isEmpty={!patientData?.pageResults?.length}
                  colSpan={5}
                  onReload={getDrChronoEventHistory}
                />
                {!loading && patientData?.pageResults?.map((row: any) => {
                  const expanded = expandedIds.includes(row.patientId as number);
                  return (
                    <React.Fragment key={row.patientId}>
                      <TableRow>
                        <TableCell>
                          <IconButton
                            size='small'
                            onClick={() => toggleExpand(row.patientId as number)}
                            aria-label={expanded ? 'Collapse' : 'Expand'}
                          >
                            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        </TableCell>

                        <TableCell>
                          <span
                            onClick={() => rowSelect({ row })}
                            style={{
                              color: '#1976d2',
                              cursor: 'pointer',
                              textDecoration: 'underline',
                            }}
                          >
                            {row.patientId}
                          </span>
                        </TableCell>
                        <TableCell>{row.patientName}</TableCell>
                        <TableCell>{row.jobCount}</TableCell>
                        <TableCell align='center'>
                          <IconButton
                            size='small'
                            onClick={() => jobRetry(row.patientId)}
                            title='Retry'
                          >
                            <ReplayIcon fontSize='small' color='warning' />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                      {expanded ? (
                        <TableRow>
                          <TableCell colSpan={6}>
                            <Collapse in={expanded} timeout='auto' unmountOnExit>
                              <Box sx={{ margin: 2, borderRadius: 1, p: 1 }}>
                                {(!row.jobs || row.jobs.length === 0) && (
                                  <Typography variant='body2'>No jobs available</Typography>
                                )}
                                {row.jobs &&
                                  row.jobs.map((job: any, idx: number) => (
                                    <Paper
                                      key={job.id ?? idx}
                                      sx={{
                                        p: 2,
                                        my: 1,
                                        borderRadius: 1,
                                        border: '1px solid #95a984ff',
                                      }}
                                      elevation={5}
                                    >
                                      <Stack
                                        direction={{ xs: 'column', sm: 'row', md: 'row' }}
                                        spacing={2}
                                        alignItems='center'
                                        justifyContent='space-between'
                                      >
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            flexWrap: 'wrap',
                                            gap: 2,
                                          }}
                                        >
                                          <Typography variant='body2'>
                                            <strong>Job No:</strong>&nbsp;
                                            <span style={{ fontFamily: 'monospace' }}>
                                              {job.jobId}
                                            </span>
                                          </Typography>
                                          <Typography variant='body2'>
                                            <strong>Event Name:</strong>&nbsp;{job.eventType}
                                          </Typography>

                                          <Typography variant='body2'>
                                            <strong>Retry Count:</strong>&nbsp;{job.retryCount}
                                          </Typography>
                                          <Typography
                                            variant='body2'
                                            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                                          >
                                            <strong>Job Status:</strong>
                                            <Chip
                                              label={job.status}
                                              size='small'
                                              sx={{ height: 22 }}
                                            />
                                          </Typography>
                                          <Typography variant='body2'>
                                            <strong>Created:</strong>&nbsp;
                                            {formatDate(job.createdDate)}
                                          </Typography>
                                        </Box>

                                        {/* small action icons */}
                                        <Stack direction='row' spacing={1} alignItems='center'>
                                          <IconButton
                                            size='small'
                                            onClick={() => copyJob(job)}
                                            title='Copy'
                                          >
                                            <ContentCopyIcon fontSize='small' color='success' />
                                          </IconButton>
                                          {/* MM-1088: archive this one job */}
                                          {isArchivable(job.eventType) && (
                                            <IconButton
                                              size='small'
                                              onClick={() =>
                                                setArchiveTarget({ job, patientId: row.patientId })
                                              }
                                              title='Archive'
                                            >
                                              <ArchiveOutlinedIcon fontSize='small' color='error' />
                                            </IconButton>
                                          )}
                                        </Stack>
                                      </Stack>

                                      {/* Divider */}
                                      <Divider sx={{ my: 1 }} />

                                      {/* Failure message - split into numbered paragraphs (function splitFailure required) */}
                                      <Box>
                                        <Typography variant='body2' sx={{ mb: 0.5 }}>
                                          <strong>Failure:</strong>
                                        </Typography>

                                        <Box sx={{ pl: 4, backgroundColor: '#deedc4ff' }}>
                                          {splitFailure(job.failureMessage).map(
                                            (part: string, i: number) => (
                                              <Typography
                                                key={i}
                                                variant='body2'
                                                sx={{
                                                  mb: 0.5,
                                                  whiteSpace: 'pre-wrap',
                                                  wordBreak: 'break-word',
                                                }}
                                              >
                                                {part}
                                              </Typography>
                                            )
                                          )}
                                        </Box>
                                      </Box>
                                    </Paper>
                                  ))}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Grid>
      <ArchiveJobDialog
        job={archiveTarget?.job ?? null}
        archiving={archiving}
        onCancel={() => setArchiveTarget(null)}
        onConfirm={archiveJob}
      />
    </Container>
  );
}
